import React, { useState, useEffect } from 'react';
import ProductGrid from '../../components/ProductGrid';

interface FilterCategory {
    id: string;
    name: string;
    filters: { id: string; name: string }[];
}

const Home: React.FC = () => {
    const [filterCategories, setFilterCategories] = useState<FilterCategory[]>([]);
    const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
    const [expandedCategories, setExpandedCategories] = useState<{ [key: string]: boolean }>({});

    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/filter-categories`)
            .then((response) => response.json())
            .then((data) => {
                const sortedCategories = data.map((category: FilterCategory) => ({
                    ...category,
                    filters: category.filters.sort((a, b) => a.name.localeCompare(b.name))
                }));
                setFilterCategories(sortedCategories);
            })
            .catch((error) => console.error('Error fetching filter categories:', error));
    }, []);

    const handleFilterChange = (filterName: string, checked: boolean) => {
        const normalizedFilter = filterName.toLowerCase();
        setSelectedFilters((prevFilters) => 
            checked ? [...prevFilters, normalizedFilter] : prevFilters.filter((filter) => filter !== normalizedFilter)
        );
    };

    const toggleCategoryExpansion = (categoryId: string) => {
        setExpandedCategories((prev) => ({
            ...prev,
            [categoryId]: !prev[categoryId]
        }));
    };

    return (
        <div className="min-h-[calc(100vh-130px)] flex flex-col bg-gray-900 text-white">
            <main className="flex flex-1 justify-center">
                <div className="flex w-4/5 h-fit bg-gradient-to-r from-slate-800 to-gray-800 rounded-lg shadow-lg overflow-hidden mt-8 mb-8 min-h-[calc(100vh-200px)]">
                    <aside className="w-1/6 bg-gradient-to-r from-slate-800 to-gray-800 p-4 border-r-2 border-slate-700 min-h-full shadow-2xl">
                        {filterCategories.map((category) => (
                            <div key={category.id} className="mb-4">
                                <h3 className="font-semibold uppercase underline underline-offset-4 decoration-slate-300 decoration-2 pb-2" style={{ fontFamily: 'Montserrat, serif' }}>{category.name}</h3>
                                <ul className={`custom-scrollbar ${expandedCategories[category.id] ? 'max-h-48 overflow-y-auto' : ''}`}>
                                    {category.filters.slice(0, expandedCategories[category.id] ? category.filters.length : 5).map((filter) => (
                                        <li key={filter.id} className="mb-1">
                                            <label htmlFor={filter.id} className="flex flex-row items-center gap-2.5 text-slate-300 cursor-pointer">
                                                <input
                                                    id={filter.id}
                                                    type="checkbox"
                                                    className="peer hidden"
                                                    checked={selectedFilters.includes(filter.name.toLowerCase())}
                                                    onChange={(e) => handleFilterChange(filter.name, e.target.checked)}
                                                />
                                                <div className="h-4 w-4 flex items-center justify-center rounded-sm border-2 border-slate-600 bg-slate-950 peer-checked:bg-slate-200 peer-checked:border-slate-400 transition duration-200 ease-in-out" />
                                                <span className="text-slate-300 uppercase font-semibold text-sm" style={{ fontFamily: 'Montserrat, serif' }}>{filter.name}</span>
                                            </label>
                                        </li>
                                    ))}
                                </ul>
                                {category.filters.length > 5 && (
                                    <button
                                        onClick={() => toggleCategoryExpansion(category.id)}
                                        className="text-sm font-semibold text-sky-600 hover:underline" style={{ fontFamily: 'Montserrat, serif' }}
                                    >
                                        {expandedCategories[category.id] ? 'Minimize' : `${category.filters.length - 5} More`}
                                    </button>
                                )}
                            </div>
                        ))}
                    </aside>

                    <section className="w-full p-4">
                        <ProductGrid selectedFilters={selectedFilters} />
                    </section>
                </div>
            </main>
        </div>
    );
};

export default Home;